/**
 * Users Page - Client User Administration
 *
 * CRITICAL: Follows Tim-Combo Pattern (MANDATORY)
 * - Uses useStatus() hook for authentication state
 * - Uses useEffect for side effects and routing
 * - Single source of truth (StatusProvider)
 * - No onSuccess callbacks
 * - Error_Message from StatusProvider
 *
 * This is NOT about authentication (login/logout).
 * This is about client administrators managing their team members' accounts and permissions.
 *
 * Standards: .equilateral-standards/frontend-development/tim_combo_frontend_auth_pattern.md
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Label, TextInput, Select, Badge, Spinner, Alert } from 'flowbite-react';
import { HiPlus, HiPencil, HiTrash, HiEye, HiKey } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusProvider';
import {
  User,
  UserEntitlement,
  createUser,
  updateUser,
  deleteUser,
  getAllUsers,
  getUserEntitlements,
  isSuccessResponse,
  CreateUserRequest,
  UpdateUserRequest
} from '../services/api/users';
import UserEntitlementsModal from '../components/users/UserEntitlementsModal';

const getUserStatusBadgeColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'failure';
    case 'suspended':
      return 'warning';
    default:
      return 'gray';
  }
};

const UsersPage: React.FC = () => {
  // Tim-Combo Pattern: Use StatusProvider for auth state
  const { Auth_Status, Is_Super_Admin, Error_Message: statusError } = useStatus();

  // Local state for user management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEntitlementsModal, setShowEntitlementsModal] = useState(false);

  // Form states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userEntitlements, setUserEntitlements] = useState<UserEntitlement[]>([]);
  const [formData, setFormData] = useState<CreateUserRequest>({
    Email_Address: '',
    Client_ID: '',
    User_Display_Name: '',
    First_Name: '',
    Last_Name: '',
    User_Status: 'active',
  });

  // Filter states
  const [filters, setFilters] = useState({
    email: '',
    status: '',
  });

  // Tim-Combo Pattern: useEffect for side effects (data fetching)
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAllUsers();

      if (isSuccessResponse(response)) {
        // Apply client-side filters
        let filteredUsers = response.data.Records;

        if (filters.email) {
          filteredUsers = filteredUsers.filter(u =>
            u.Email_Address.toLowerCase().includes(filters.email.toLowerCase())
          );
        }

        if (filters.status) {
          filteredUsers = filteredUsers.filter(u =>
            u.User_Status.toLowerCase() === filters.status.toLowerCase()
          );
        }

        setUsers(filteredUsers);
      } else {
        setError(response.message || 'Failed to load users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (Auth_Status) {
      fetchUsers();
    }
  }, [Auth_Status, fetchUsers]);

  const fetchUserEntitlements = async (email: string) => {
    try {
      const response = await getUserEntitlements(email);

      if (isSuccessResponse(response)) {
        setUserEntitlements(response.data.Records);
        setShowDetailsModal(true);
      } else {
        setError(response.message || 'Failed to load user entitlements');
      }
    } catch (err) {
      console.error('Error fetching user entitlements:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user entitlements');
    }
  };

  const handleCreate = async () => {
    setError(null);

    try {
      const response = await createUser(formData);

      if (isSuccessResponse(response)) {
        setShowCreateModal(false);
        resetForm();
        // Tim-Combo Pattern: Update state, then refetch
        await fetchUsers();
      } else {
        setError(response.message || 'Failed to create user');
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleEdit = async () => {
    if (!selectedUser) return;

    setError(null);

    try {
      const updateData: UpdateUserRequest = {
        Email_Address: selectedUser.Email_Address,
        User_Display_Name: formData.User_Display_Name,
        First_Name: formData.First_Name,
        Last_Name: formData.Last_Name,
        User_Status: formData.User_Status,
      };

      const response = await updateUser(updateData);

      if (isSuccessResponse(response)) {
        setShowEditModal(false);
        resetForm();
        // Tim-Combo Pattern: Update state, then refetch
        await fetchUsers();
      } else {
        setError(response.message || 'Failed to update user');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    setError(null);

    try {
      const response = await deleteUser(selectedUser.Email_Address);

      if (isSuccessResponse(response)) {
        setShowDeleteModal(false);
        setSelectedUser(null);
        // Tim-Combo Pattern: Update state, then refetch
        await fetchUsers();
      } else {
        setError(response.message || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      Email_Address: user.Email_Address,
      Client_ID: user.Client_ID,
      User_Display_Name: user.User_Display_Name || '',
      First_Name: user.First_Name || '',
      Last_Name: user.Last_Name || '',
      User_Status: user.User_Status,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openEntitlementsModal = (user: User) => {
    setSelectedUser(user);
    setShowEntitlementsModal(true);
  };

  const resetForm = () => {
    setFormData({
      Email_Address: '',
      Client_ID: '',
      User_Display_Name: '',
      First_Name: '',
      Last_Name: '',
      User_Status: 'active',
    });
    setError(null);
  };

  // Tim-Combo Pattern: Display StatusProvider errors
  const displayError = error || statusError;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          User Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage user accounts and their client/company access permissions
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="filterEmail">Filter by Email</Label>
          <TextInput
            id="filterEmail"
            placeholder="Search email..."
            value={filters.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="filterStatus">Filter by Status</Label>
          <Select
            id="filterStatus"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </Select>
        </div>
        <div className="flex items-end">
          <Button color="blue" onClick={openCreateModal}>
            <HiPlus className="mr-2 h-5 w-5" />
            Create User
          </Button>
        </div>
      </div>

      {/* Tim-Combo Pattern: Error display from state */}
      {displayError && (
        <Alert color="failure" className="mb-4">
          {displayError}
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner size="xl" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          No users found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>Email</Table.HeadCell>
              <Table.HeadCell>Display Name</Table.HeadCell>
              <Table.HeadCell>Name</Table.HeadCell>
              <Table.HeadCell>Client ID</Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell>Super Admin</Table.HeadCell>
              <Table.HeadCell>Last Login</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {users.map((user) => (
                <Table.Row key={user.Email_Address} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                  <Table.Cell className="font-medium text-gray-900 dark:text-white">
                    {user.Email_Address}
                  </Table.Cell>
                  <Table.Cell>{user.User_Display_Name || '-'}</Table.Cell>
                  <Table.Cell>
                    {user.First_Name && user.Last_Name
                      ? `${user.First_Name} ${user.Last_Name}`
                      : user.First_Name || user.Last_Name || '-'}
                  </Table.Cell>
                  <Table.Cell>
                    <code className="text-xs">{user.Client_ID}</code>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={getUserStatusBadgeColor(user.User_Status)}>
                      {user.User_Status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {user.Super_Admin ? (
                      <Badge color="purple">Yes</Badge>
                    ) : (
                      <Badge color="gray">No</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {user.Last_Login
                      ? new Date(user.Last_Login).toLocaleDateString()
                      : 'Never'}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-2">
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => fetchUserEntitlements(user.Email_Address)}
                        title="View Details"
                      >
                        <HiEye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => openEntitlementsModal(user)}
                        title="Manage Entitlements"
                      >
                        <HiKey className="h-4 w-4" />
                      </Button>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => openEditModal(user)}
                        title="Edit User"
                      >
                        <HiPencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="xs"
                        color="failure"
                        onClick={() => openDeleteModal(user)}
                        disabled={user.Super_Admin}
                        title={user.Super_Admin ? 'Cannot delete super admin' : 'Delete User'}
                      >
                        <HiTrash className="h-4 w-4" />
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Create Modal */}
      <Modal show={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <Modal.Header>Create User</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email *</Label>
              <TextInput
                id="email"
                type="email"
                value={formData.Email_Address}
                onChange={(e) => setFormData({ ...formData, Email_Address: e.target.value })}
                placeholder="Enter email address"
                required
              />
            </div>
            <div>
              <Label htmlFor="clientId">Client ID *</Label>
              <TextInput
                id="clientId"
                value={formData.Client_ID}
                onChange={(e) => setFormData({ ...formData, Client_ID: e.target.value })}
                placeholder="Enter client ID"
                required
              />
            </div>
            <div>
              <Label htmlFor="displayName">Display Name</Label>
              <TextInput
                id="displayName"
                value={formData.User_Display_Name}
                onChange={(e) => setFormData({ ...formData, User_Display_Name: e.target.value })}
                placeholder="Enter display name"
              />
            </div>
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <TextInput
                id="firstName"
                value={formData.First_Name}
                onChange={(e) => setFormData({ ...formData, First_Name: e.target.value })}
                placeholder="Enter first name"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <TextInput
                id="lastName"
                value={formData.Last_Name}
                onChange={(e) => setFormData({ ...formData, Last_Name: e.target.value })}
                placeholder="Enter last name"
              />
            </div>
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select
                id="status"
                value={formData.User_Status}
                onChange={(e) => setFormData({ ...formData, User_Status: e.target.value })}
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleCreate}
            disabled={!formData.Email_Address || !formData.Client_ID}
          >
            Create
          </Button>
          <Button color="gray" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)}>
        <Modal.Header>Edit User</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editEmail">Email</Label>
              <TextInput
                id="editEmail"
                type="email"
                value={formData.Email_Address}
                disabled
              />
              <p className="text-sm text-gray-500 mt-1">Email cannot be changed</p>
            </div>
            <div>
              <Label htmlFor="editDisplayName">Display Name</Label>
              <TextInput
                id="editDisplayName"
                value={formData.User_Display_Name}
                onChange={(e) => setFormData({ ...formData, User_Display_Name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="editFirstName">First Name</Label>
              <TextInput
                id="editFirstName"
                value={formData.First_Name}
                onChange={(e) => setFormData({ ...formData, First_Name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="editLastName">Last Name</Label>
              <TextInput
                id="editLastName"
                value={formData.Last_Name}
                onChange={(e) => setFormData({ ...formData, Last_Name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="editStatus">Status *</Label>
              <Select
                id="editStatus"
                value={formData.User_Status}
                onChange={(e) => setFormData({ ...formData, User_Status: e.target.value })}
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleEdit}>
            Save Changes
          </Button>
          <Button color="gray" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Details Modal */}
      <Modal
        show={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        size="3xl"
      >
        <Modal.Header>User Entitlements</Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                <div>
                  <Label>Email</Label>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {selectedUser.Email_Address}
                  </p>
                </div>
                <div>
                  <Label>Display Name</Label>
                  <p className="text-gray-900 dark:text-white">
                    {selectedUser.User_Display_Name || '-'}
                  </p>
                </div>
              </div>

              <div>
                <Label>Client & Company Access</Label>
                {userEntitlements.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {userEntitlements.map((entitlement) => (
                      <div
                        key={`${entitlement.Client_ID}-${entitlement.Company_ID}`}
                        className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {entitlement.Company_Name || entitlement.Company_ID}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {entitlement.Client_Name || entitlement.Client_ID}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {entitlement.Admin && <Badge color="failure">Admin</Badge>}
                            {entitlement.Approver && <Badge color="warning">Approver</Badge>}
                            {entitlement.Executive && <Badge color="success">Executive</Badge>}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <p>Granted: {new Date(entitlement.Granted_At).toLocaleDateString()}</p>
                          {entitlement.Expires_At && (
                            <p>Expires: {new Date(entitlement.Expires_At).toLocaleDateString()}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 mt-2">
                    No entitlements granted
                  </p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="md">
        <Modal.Header>Deactivate User</Modal.Header>
        <Modal.Body>
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to deactivate <strong>{selectedUser?.Email_Address}</strong>?
          </p>
          <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
            This will set the user status to "inactive". The user will no longer be able to access the system.
            This action can be reversed by updating the user status.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleDelete}>
            Deactivate
          </Button>
          <Button color="gray" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User Entitlements Modal */}
      {selectedUser && (
        <UserEntitlementsModal
          show={showEntitlementsModal}
          onClose={() => setShowEntitlementsModal(false)}
          user={selectedUser}
          onUpdate={() => {
            setShowEntitlementsModal(false);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default UsersPage;
